import
     {
    useCallback,
    useContext,
} from "react";
import {FormData} from "../components/form-renderer/FormProvider";
import ElementRenderer
    from "../components/form-renderer/ElementRenderer";
import {
    Tooltip
} from "@mui/material";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import styled
    from 'styled-components';
import IconButton
    from "@mui/material/IconButton";

/**
 *
 * @param index         returns the final number of an index (i.e. returns the 5 in the index "0_5")
 * @returns {number}
 */
const parseIndex = (index) => {
    return index && index.substr(2) ? parseInt(index.substr(2)) : 0;
}

const parseIndexInSubheader = (index, subheaderRow) => {
    return parseInt(index.replace(`${subheaderRow}_`, ''));
}

const TableRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  width: 100%;
`;

const TableButtonContainer = styled.div`
  flex: 0 0 auto;
`;

const RepeatableFieldContainer = styled.div`
  flex: 1 0 auto;
`

const findSubheaderRowIndexes = (indexes, subheaderRow) => {
    const prefix = `${subheaderRow}_`;
    const found = indexes.filter(index => index.indexOf(prefix) > -1);

    // console.log({
    //     'method': 'findSubheaderRowIndexes',
    //     prefix,
    //     indexes,
    //     found
    // })

    return found;
}

const buildChildFieldsArray = (repeatableChildren) => {
    const childFields = [];

    if (Object.keys(repeatableChildren).length > 0) {
        Object.keys(repeatableChildren).map(key => {
            childFields.push(repeatableChildren[key]);
        });
    }

    return childFields;
}

export default function useRepeatables({
                                           template,
                                           defaultAnswer,
                                           childProperty = 'children',
                                           defaultRows = 0,
                                           buttonText = 'Add row',
                                           subheaderRow = 0,
                                           type = 'repeatable',
                                           showButtons = true
                                       }) {
    let {
        state,
        dispatch,
        findAnswer
    } = useContext(FormData);

    const children = template?.[childProperty];
    const childFields = buildChildFieldsArray(children);

    let rows = defaultRows;
    let indexes;
    const firstField = Object.values(childFields)[0];

    if (firstField && firstField.id && state.values[firstField.id]) {
        rows = findSubheaderRowIndexes(Object.keys(state.values[firstField.id]), subheaderRow).length;
        indexes = findSubheaderRowIndexes(Object.keys(state.values[firstField.id]), subheaderRow);
    }

    const lastIndex = indexes ? parseIndex(indexes[indexes.length - 1]) : 0;

    // console.log({
    //     indexes,
    //     lastIndex
    // })

    const addRow = (e) => {
        e.preventDefault();

        for (const childField of childFields) {
            dispatch({
                type: 'APPEND_FIELD_VALUE',
                payload: {
                    field: childField,
                    answerIndex: `${subheaderRow}_${lastIndex + 1}`,
                    value: {
                        id: childField.id,
                        repeatedCount: 0,
                        type: childField.objecttype,
                        value: {},
                        visible: childField?.content?.visible === true,
                        objectname: childField.name
                    }
                }
            });
        }

        dispatch({
            type: 'APPEND_TO_VALUE_PROPERTY',
            payload: {
                field: template,
                answerIndex: subheaderRow,
                property: 'entries',
                value: `${subheaderRow}_${lastIndex + 1}`
            }
        })
    }

    const addChildFieldValues = (repeatableChildren, subheaderRow = 0, suffixIndex = false) => {
        let indexes;
        const childFields = buildChildFieldsArray(repeatableChildren);
        const firstField = Object.values(childFields)[0];

        if (firstField && firstField.id && state.values[firstField.id]) {
            indexes = findSubheaderRowIndexes(Object.keys(state.values[firstField.id]), subheaderRow);
        }

        const lastIndex = indexes.length > 0 ? parseIndex(indexes[indexes.length - 1]) : 0;

        for (const childField of childFields) {
            dispatch({
                type: 'APPEND_FIELD_VALUE',
                payload: {
                    field: childField,
                    answerIndex: `${subheaderRow}${suffixIndex ? '_1' : ''}`,
                    value: {
                        id: childField.id,
                        repeatedCount: 0,
                        type: childField.objecttype,
                        value: {},
                        visible: childField?.content?.visible === true,
                        objectname: childField.name
                    }
                }
            });
        }

        dispatch({
            type: 'APPEND_FIELD_VALUE',
            payload: {
                field: template,
                answerIndex: subheaderRow,
                value: {
                    id: template.id,
                    repeatedCount: subheaderRow,
                    type: template.objecttype,
                    value: {},
                    visible: false,
                    objectname: template.name
                }
            }
        });

        // dispatch({
        //     type: 'APPEND_TO_VALUE_PROPERTY',
        //     payload: {
        //         field: template,
        //         answerIndex: `0`,
        //         property: 'entries',
        //         value: `${subheaderRow}_${lastIndex + 1}`
        //     }
        // })
    }

    const deleteRow = (e, row) => {
        e.preventDefault();

        for (const childField of childFields) {
            dispatch({
                type: 'DELETE_VALUE',
                payload: {
                    field: childField,
                    answerIndex: row,
                }
            })
        }

        dispatch({
            type: 'DELETE_VALUE_PROPERTY_ENTRY',
            payload: {
                field: template,
                answerIndex: subheaderRow,
                property: 'entries',
                value: row
            }
        })
    }

    const renderElements = (i) => {
        if (Array.isArray(childFields) && childFields.length > 0) {
            return childFields.map((childField, index) => {
                return <ElementRenderer answerIndex={i} key={index} template={childField} defaultAnswer={defaultAnswer}/>
            });
        }
    }

    const childElements = useCallback((buttonRenderer = null, visibleRow = undefined) => {
        const r = [];
        let current = 1;

        let RowElement = type === 'repeatable' ? 'div' : TableRow;
        let ButtonContainer = type === 'repeatable' ? 'div' : TableButtonContainer;
        const displayClass = type === 'repeatable' ? 'block' : 'flex';

        if (rows > 0 && firstField) {
            for (const key of findSubheaderRowIndexes(Object.keys(state.values[firstField.id]), subheaderRow)) {
                r.push((
                    <RowElement style={{display: (visibleRow === undefined || visibleRow === current) ? displayClass : 'none'}} key={key} className='fz-form-repeatable-row'>
                        {/* Custom Button Renderer */}
                        {buttonRenderer && buttonRenderer({
                            onDelete: (e) => deleteRow(e, key),
                            onAdd: (e) => addRow(e)
                        })}

                        <RepeatableFieldContainer>
                            {renderElements(key)}
                        </RepeatableFieldContainer>

                        {/* Custom Button Renderer */}
                        {buttonRenderer && buttonRenderer({
                            onDelete: (e) => deleteRow(e, key),
                            onAdd: (e) => addRow(e)
                        })}

                        {/* Default Buttons */}
                        {(!buttonRenderer && template.content?.disableDelete !== true && showButtons === true) && (
                            <ButtonContainer>
                                {template.content?.disableDelete !== true && (
                                    <Tooltip title="Delete row"><IconButton className='fz-form-button fz-form-repeatable-button fz-form-repeatable-delete-button' sx={{marginTop: '8px', backgroundColor: '#eee'}} onClick={(e) => deleteRow(e, key)}><DeleteOutlined/></IconButton></Tooltip>
                                )}
                            </ButtonContainer>
                        )}
                    </RowElement>
                ));

                current++;
            }
        }

        return r;
    }, [rows]);

    return {
        addRow,
        childElements,
        rows,
        addChildFieldValues
    }
}